import { useState, useRef, useEffect } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Bot, Send, Trash2, Building2 } from "lucide-react";
import { CopilotMessageList } from "./CopilotMessageList";
import { CopilotActionCard } from "./CopilotActionCard";
import { CopilotConfirmDialog } from "./CopilotConfirmDialog";
import { useCopilot, type CopilotTenantContext } from "@/hooks/useCopilot";

interface CopilotDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTenantContext?: CopilotTenantContext | null;
}

const STARTER_PROMPTS = [
  "Show me all open incidents",
  "Which tenants have critical issues?",
  "Summarize current platform health",
];

export function CopilotDrawer({ open, onOpenChange, initialTenantContext }: CopilotDrawerProps) {
  const {
    messages,
    isLoading,
    tenantContext,
    pendingAction,
    sendMessage,
    confirmAction,
    setTenantContext,
    setPendingAction,
    clearMessages,
  } = useCopilot();

  const [input, setInput] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && initialTenantContext) {
      setTenantContext(initialTenantContext);
    }
  }, [open, initialTenantContext, setTenantContext]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isLoading) return;
    setInput("");
    sendMessage(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col p-0">
          {/* Header */}
          <SheetHeader className="border-b px-4 py-3 space-y-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2 text-sm">
                <div className="rounded-lg bg-primary/10 p-1.5">
                  <Bot className="h-4 w-4 text-primary" />
                </div>
                Platform Copilot
              </SheetTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={clearMessages}
                title="Clear conversation"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
            {tenantContext && (
              <div className="flex items-center gap-1.5 mt-2">
                <Badge variant="outline" className="text-[10px]">
                  <Building2 className="h-2.5 w-2.5 mr-1" />
                  {tenantContext.companyName}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 text-[10px] px-1"
                  onClick={() => setTenantContext(null)}
                >
                  ✕
                </Button>
              </div>
            )}
          </SheetHeader>

          {/* Messages */}
          <CopilotMessageList messages={messages} isLoading={isLoading} />

          {/* Pending action card */}
          {pendingAction && (
            <div className="px-4 pb-2">
              <CopilotActionCard
                action={pendingAction}
                onConfirm={() => setShowConfirmDialog(true)}
                onDecline={() => setPendingAction(null)}
              />
            </div>
          )}

          {/* Starter prompts */}
          {messages.length === 0 && !isLoading && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5">
              {STARTER_PROMPTS.map((prompt) => (
                <Badge
                  key={prompt}
                  variant="outline"
                  className="cursor-pointer text-xs hover:bg-accent transition-colors"
                  onClick={() => sendMessage(prompt)}
                >
                  {prompt}
                </Badge>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="border-t p-3">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                placeholder="Ask about incidents, tenants, or platform health…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                className="text-sm"
              />
              <Button
                size="icon"
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <CopilotConfirmDialog
        action={pendingAction}
        open={showConfirmDialog}
        onConfirm={() => {
          setShowConfirmDialog(false);
          if (pendingAction) confirmAction(pendingAction);
        }}
        onCancel={() => setShowConfirmDialog(false)}
      />
    </>
  );
}
